// Location: src/pages/plan-summary/components/AchievementsBadges.tsx

import { useTranslation } from 'react-i18next';

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  unlocked_at: string;
}

interface AchievementsBadgesProps {
  achievements: Achievement[];
}

export default function AchievementsBadges({ achievements }: AchievementsBadgesProps) {
  const { t, i18n } = useTranslation();

  if (!achievements || achievements.length === 0) {
    return null;
  }

  const getTrophyStyle = (color: string) => {
    switch (color) {
      case 'yellow': return { icon: 'text-yellow-400', glow: 'bg-yellow-500' };
      case 'orange': return { icon: 'text-orange-400', glow: 'bg-orange-500' };
      case 'green': return { icon: 'text-green-400', glow: 'bg-green-500' };
      case 'blue': return { icon: 'text-blue-400', glow: 'bg-blue-500' };
      case 'purple': return { icon: 'text-purple-400', glow: 'bg-purple-500' };
      case 'red': return { icon: 'text-red-400', glow: 'bg-red-500' };
      case 'pink': return { icon: 'text-pink-400', glow: 'bg-pink-500' };
      default: return { icon: 'text-gray-400', glow: 'bg-gray-500' };
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-6 lg:p-8 backdrop-blur-sm h-full flex flex-col">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="text-xl font-light text-white tracking-tight">{t('planSummary.achievements.title')}</h3>
          <p className="text-xs text-gray-500 mt-1">{t('planSummary.achievements.subtitle')}</p>
        </div>
        <div className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full flex items-center gap-2">
          <i className="ri-medal-2-fill text-yellow-400"></i>
          <span className="text-xs text-white font-medium">{achievements.length}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 flex-grow">
        {achievements.map((achievement) => {
          const style = getTrophyStyle(achievement.color);

          return (
            <div
              key={achievement.id}
              className="relative group bg-white/[0.01] border border-white/5 rounded-2xl p-5 hover:bg-white/[0.03] hover:border-white/10 transition-all duration-500 overflow-hidden cursor-default"
            >
              {/* Glass Trophy Ambient Glow */}
              <div className={`absolute -top-10 -right-10 w-32 h-32 ${style.glow} opacity-10 rounded-full blur-3xl group-hover:opacity-20 transition-opacity duration-500`}></div>
              
              <div className="relative z-10 flex flex-col h-full">
                <div className={`w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-4 ${style.icon} group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(255,255,255,0.05)] transition-all duration-500`}>
                  <i className={`${achievement.icon} text-2xl`}></i>
                </div>

                <div className="flex-grow">
                  <div className="text-white text-sm font-medium mb-1.5 tracking-tight">
                    {achievement.title}
                  </div>
                  <div className="text-gray-400 text-xs font-light line-clamp-2 leading-relaxed">
                    {achievement.description}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                  <span className="text-[9px] uppercase tracking-widest text-gray-600">{t('planSummary.achievements.unlockedLabel')}</span>
                  <span className="text-[10px] text-gray-500 font-medium">{formatDate(achievement.unlocked_at)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 pt-6 border-t border-white/5">
        <div className="bg-yellow-500/[0.02] border border-yellow-500/10 rounded-2xl p-4 flex items-center gap-4">
          <div className="w-8 h-8 rounded-full bg-yellow-500/10 text-yellow-400 flex items-center justify-center shrink-0">
            <i className="ri-sparkling-fill"></i>
          </div>
          <div>
            <div className="text-[10px] text-yellow-400/80 uppercase tracking-widest font-medium mb-0.5">
              {t('planSummary.achievements.celebration.title')}
            </div>
            <div className="text-gray-400 text-xs font-light">
              {achievements.length === 1 
                ? t('planSummary.achievements.celebration.message', { count: achievements.length })
                : t('planSummary.achievements.celebration.messagePlural', { count: achievements.length })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}